import { useEffect, useLayoutEffect, useRef, useState } from "react";
import Icon from "./Icon.jsx";

// Themed right-click menu. Items can be:
//   { label, icon?, onClick, disabled?, tone?: "default"|"cyan"|"coral", shortcut? }
//   { divider: true }
// The menu is positioned at (x, y) but nudged inside the viewport so it
// never clips off-screen.

export default function ContextMenu({ x, y, items, onClose }) {
  const ref = useRef(null);
  const [pos, setPos] = useState({ left: x, top: y });

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    let left = x;
    let top = y;
    if (left + rect.width > window.innerWidth - 8) {
      left = Math.max(8, window.innerWidth - rect.width - 8);
    }
    if (top + rect.height > window.innerHeight - 8) {
      top = Math.max(8, window.innerHeight - rect.height - 8);
    }
    setPos({ left, top });
  }, [x, y, items]);

  useEffect(() => {
    const onDown = (e) => {
      if (ref.current && ref.current.contains(e.target)) return;
      onClose();
    };
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    // Defer attaching mousedown so the same right-click that opened the
    // menu doesn't immediately close it.
    const t = setTimeout(() => {
      document.addEventListener("mousedown", onDown);
      document.addEventListener("contextmenu", onDown);
    }, 0);
    document.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(t);
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("contextmenu", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  return (
    <div
      ref={ref}
      role="menu"
      style={{ position: "fixed", left: pos.left, top: pos.top, zIndex: 100 }}
      className="min-w-[15rem] rounded-2xl border-2 border-ink bg-paper py-1.5 shadow-plump"
      onClick={(e) => e.stopPropagation()}
      onContextMenu={(e) => e.preventDefault()}
    >
      {items.map((item, i) => {
        if (item.divider) {
          return <div key={`d-${i}`} className="my-1 mx-2 h-0.5 rounded-full bg-paper-edge" />;
        }
        const tone = item.tone || "default";
        const toneCls =
          tone === "coral"
            ? "text-ink hover:bg-coral-soft"
            : tone === "cyan"
            ? "text-ink hover:bg-cyan-soft"
            : "text-ink hover:bg-paper-deep";
        return (
          <button
            key={i}
            role="menuitem"
            disabled={item.disabled}
            onClick={() => { if (!item.disabled) { item.onClick(); onClose(); } }}
            className={
              "flex w-full items-center gap-2.5 px-3 py-1.5 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-40 " +
              toneCls
            }
          >
            <span className="flex h-5 w-5 shrink-0 items-center justify-center">
              {item.icon ? <Icon name={item.icon} size={18} alt="" /> : null}
            </span>
            <span className="flex-1 text-left">{item.label}</span>
            {item.shortcut && (
              <span className="text-[11px] tabular-nums text-ink-faint">{item.shortcut}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}
